/**
 * Platform event types matching the Kodeon event schema
 */

import * as tokens from '@/lib/tokens'

export type EventCategory = 'activity' | 'telemetry' | 'lifecycle' | 'operational' | 'communication'
export type EventOrigin = 'platform' | 'client' | 'external'
export type EventStorage = 'realtime' | 'analytics'

export interface PlatformEvent {
  id: string
  object: 'event'
  api_version: string
  created: number // Unix timestamp
  livemode: boolean
  type: string // e.g., "applife.signed_up", "customer.created", "purchase.completed"
  category: EventCategory
  storage: EventStorage
  origin: EventOrigin
  source: string // URN format: "urn:kodeon:service:support"
  customer_id: string | null
  related_customer_ids?: string[]
  related_events?: string[]
  occurred_at: number // Unix timestamp
  resource_id: string | null
  resource_name: string | null
  session_id: string | null
  timezone: string | null
  idempotency_key: string | null
  context: Record<string, unknown>
  data: {
    object: unknown
    previous_attributes: Record<string, unknown> | null
  }
}

export interface EventFilter {
  /** Free-text search query */
  q?: string
  type?: string | string[]
  category?: EventCategory | EventCategory[]
  origin?: EventOrigin | EventOrigin[]
  customerId?: string
  source?: string | string[]
  sessionId?: string
  /** ISO string or Unix timestamp for range start */
  from?: string | number
  /** ISO string or Unix timestamp for range end */
  to?: string | number
  createdAfter?: number
  createdBefore?: number
}

export interface EventStreamStats {
  eventsPerSecond: number
  totalEvents: number
  uniqueCustomers: number
  topEventTypes: { type: string; count: number }[]
}

/** Paginated event list response from the API. */
export interface EventListResponse {
  events: PlatformEvent[]
  cursor?: number
  has_more?: boolean
  total?: number
}

/** Response from the event types endpoint. */
export interface EventTypesResponse {
  types: string[]
}

/** SSE / WebSocket connection state. */
export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error'

/**
 * Color mapping for event categories
 * Uses CSS variables so they adapt to the active theme (dark / light)
 */
export const categoryColors: Record<EventCategory, string> = {
  activity: 'var(--category-activity)',
  telemetry: 'var(--category-telemetry)',
  lifecycle: 'var(--category-lifecycle)',
  operational: 'var(--category-operational)',
  communication: 'var(--category-communication)',
}

/**
 * Resolved hex colours for contexts that don't support CSS vars (e.g. SVG
 * `boxShadow` strings built at render-time).  Sourced from the shared token
 * map to avoid hardcoded hex literals.
 */
export const categoryHexColors: Record<EventCategory, string> = {
  activity: tokens.categoryActivity,
  telemetry: tokens.categoryTelemetry,
  lifecycle: tokens.categoryLifecycle,
  operational: tokens.categoryOperational,
  communication: tokens.categoryCommunication,
}

/**
 * Category display icons - small semantic SVG paths that distinguish each
 * category at a glance (used inside timeline dots and event badges).
 */
export const categoryIcons: Record<EventCategory, string> = {
  activity: 'M13 10V3L4 14h7v7l9-11h-7z', // lightning bolt
  telemetry: 'M22 12h-4l-3 9L9 3l-3 9H2', // pulse
  lifecycle: 'M12 22c5.52 0 10-4.48 10-10S17.52 2 12 2 2 6.48 2 12s4.48 10 10 10z', // circle
  operational:
    'M14.7 6.3a1 1 0 000 1.4l1.6 1.6-1.6 1.6a1 1 0 101.4 1.4l2.3-2.3a1 1 0 000-1.4l-2.3-2.3a1 1 0 00-1.4 0z', // gear-like
  communication: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', // chat
}

/**
 * Get a human-readable label for an event type.
 * Converts `applife.signed_up` -> `Applife -> Signed Up`
 * The function is generic and handles any `<namespace>.<event>` format.
 */
export function getEventTypeLabel(type: string): string {
  return type
    .split('.')
    .map((part) =>
      part
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    )
    .join(' -> ')
}

/**
 * Capitalise a category name for display.
 * `operational` -> `Operational`
 */
export function formatCategoryLabel(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1)
}

/**
 * Capitalise an origin name for display.
 * `platform` -> `Platform`
 */
export function formatOriginLabel(origin: string): string {
  return origin.charAt(0).toUpperCase() + origin.slice(1)
}

/**
 * Format a Unix timestamp to a relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  if (!timestamp || !Number.isFinite(timestamp) || timestamp <= 0) return 'just now'

  const now = Date.now() / 1000
  const diff = now - timestamp

  if (diff < 0) return 'just now'
  if (diff < 5) return 'now'
  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}
